import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';

import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import WorkHistoryOutlinedIcon from '@mui/icons-material/WorkHistoryOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EditCalendarOutlinedIcon from '@mui/icons-material/EditCalendarOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import KeyboardDoubleArrowLeftOutlinedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftOutlined';

import { theme } from '@bloomays-lib/ui.shared';
import { LogoutOptions, Role } from '@bloomays-lib/types.shared';

import bloomaysGlyph from '../../graphic-charter/logos/bloomays_logo.svg';

const EXPANDED_WIDTH = 264;
const COLLAPSED_WIDTH = 76;

// Palette de la sidebar sombre (brun/marron) — alignée sur la maquette Back Office.
const SIDEBAR_BG = '#2b211c';
const SIDEBAR_BORDER = 'rgba(255, 255, 255, 0.09)';
const ITEM_HOVER = 'rgba(255, 255, 255, 0.06)';
const TEXT_DEFAULT = '#d8cfc8';
const TEXT_MUTED = '#9b8d83';

// Dégradé de marque Bloomays (rouge/corail → rose) — logo + item actif.
const BRAND_GRADIENT = 'linear-gradient(135deg, #ff3328 0%, #ff6b97 100%)';
const BRAND_GLOW = 'rgba(255, 51, 40, 0.35)';
// Avatar utilisateur : maroon chaud, accordé au fond brun.
const AVATAR_GRADIENT = 'linear-gradient(135deg, #533238 0%, #6e434c 100%)';

const getInitials = (displayName: string): string =>
  displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || '?';

type NavItem = {
  key: string;
  label: string;
  icon: ReactNode;
  to?: string;
  href?: string;
  onClick?: () => void;
  /** Extra path prefixes that should also mark this item as active. */
  matchPrefixes?: string[];
};

type NavSection = {
  key: string;
  title: string;
  items: NavItem[];
};

export type SidebarProps = {
  roles: Role[];
  user?: { picture?: string; displayName: string };
  logout?: (options?: LogoutOptions) => void;
  /** Desktop collapsed (icons only) state. */
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Mobile overlay open state. */
  mobileOpen: boolean;
  onMobileClose: () => void;
};

const buildSections = (roles: Role[], t: (key: string) => string): NavSection[] => {
  const isAdmin = roles.includes(Role.Admin);
  const isSystem = roles.includes(Role.System);
  const isBloomer = roles.includes(Role.Bloomer);
  const isClient = roles.includes(Role.Client);
  // Les Bloomies = l'équipe interne Bloomays (Admin / System).
  const isBloomies = isAdmin || isSystem;

  const sections: NavSection[] = [];

  // ----- Menus des Bloomies (équipe interne) -----
  if (isBloomies) {
    sections.push({
      key: 'steering',
      title: t('sections.steering'),
      items: [
        {
          key: 'freelanceMissions',
          label: t('freelanceMissions'),
          to: '/missions-freelance',
          icon: <SpaceDashboardOutlinedIcon />,
        },
        ...(isAdmin
          ? [
              {
                key: 'planningMission',
                label: t('planningMission'),
                to: '/planningMission',
                icon: <InsightsOutlinedIcon />,
              },
            ]
          : []),
      ],
    });

    if (isBloomies) {
      sections.push({
        key: 'finance',
        title: t('sections.finance'),
        items: [
          ...(isAdmin
            ? [
                {
                  key: 'billingsActivity',
                  label: t('billingsActivity'),
                  to: '/billingsActivity',
                  icon: <ReceiptLongOutlinedIcon />,
                },
                {
                  key: 'purchaseOrders',
                  label: t('purchaseOrders'),
                  to: '/bdc',
                  icon: <RequestQuoteOutlinedIcon />,
                },
                {
                  key: 'finance',
                  label: t('finance'),
                  to: '/finance',
                  icon: <AccountBalanceOutlinedIcon />,
                },
              ]
            : []),
          {
            key: 'primes',
            label: t('primes'),
            // C-Level (System) : synthèse annuelle ; TAM (Admin) : consultation mensuelle / annuelle.
            to: '/primes',
            matchPrefixes: ['/primes'],
            icon: <SavingsOutlinedIcon />,
          },
          // PRO-1364 : extraction marge Sales / TAD (export XLS) — C-Level (System) uniquement.
          ...(isSystem
            ? [
                {
                  key: 'salesTadMargins',
                  label: t('salesTadMargins'),
                  to: '/marges-sales-tad',
                  matchPrefixes: ['/marges-sales-tad'],
                  icon: <TableChartOutlinedIcon />,
                },
              ]
            : []),
        ],
      });
    }

    if (isAdmin) {
      sections.push({
        key: 'talents',
        title: t('sections.talents'),
        items: [
          {
            key: 'talents',
            label: t('talents'),
            to: '/talents',
            matchPrefixes: ['/talentsV2'],
            icon: <PersonSearchOutlinedIcon />,
          },
          {
            key: 'jobs',
            label: t('jobs'),
            to: '/jobs',
            icon: <WorkHistoryOutlinedIcon />,
          },
        ],
      });

      sections.push({
        key: 'activity',
        title: t('sections.activity'),
        items: [
          {
            key: 'activities',
            label: t('activities'),
            to: '/activities',
            icon: <FactCheckOutlinedIcon />,
          },
          {
            key: 'contracts',
            label: t('legalContracts'),
            to: '/contracts',
            icon: <DescriptionOutlinedIcon />,
          },
        ],
      });
    }
  }

  // ----- Menus des Bloomers (freelances) -----
  if (isBloomer) {
    sections.push({
      key: 'myActivity',
      title: t('sections.myActivity'),
      items: [
        {
          key: 'dashboard',
          label: t('dashboard'),
          to: '/dashboard',
          matchPrefixes: ['/'],
          icon: <SpaceDashboardOutlinedIcon />,
        },
        {
          key: 'missions',
          label: t('missions'),
          to: '/missions',
          icon: <EditCalendarOutlinedIcon />,
        },
      ],
    });

    sections.push({
      key: 'myAccount',
      title: t('sections.myAccount'),
      items: [
        {
          key: 'meInformations',
          label: t('meInformations'),
          to: '/me/informations',
          icon: <BadgeOutlinedIcon />,
        },
        {
          key: 'settings',
          label: t('settings'),
          to: '/settings',
          icon: <SettingsOutlinedIcon />,
        },
      ],
    });
  } else if (isBloomies) {
    // Paramètres pour l'équipe interne ayant accès.
    sections.push({
      key: 'admin',
      title: t('sections.admin'),
      items: [
        ...(isAdmin
          ? [
              {
                key: 'settings',
                label: t('settings'),
                to: '/settings',
                icon: <SettingsOutlinedIcon />,
              },
            ]
          : []),
        // TEC-3302 — Formulaires de test des envois LinkedIn (POC Unipile) — System uniquement.
        ...(isSystem
          ? [
              {
                key: 'pocUnipile',
                label: t('pocUnipile'),
                to: '/poc-unipile',
                icon: <SendOutlinedIcon />,
              },
            ]
          : []),
      ],
    });
  }

  // ----- Aide (Bloomers et visiteurs) -----
  if (isBloomer || isClient) {
    sections.push({
      key: 'help',
      title: t('sections.help'),
      items: [
        {
          key: 'bloomhelp',
          label: t('help'),
          onClick: () => window?.HubSpotConversations?.widget?.open(),
          icon: <SupportAgentOutlinedIcon />,
        },
        {
          key: 'faq',
          label: t('faq'),
          href: 'https://bit.ly/bloomays-faq',
          icon: <HelpOutlineOutlinedIcon />,
        },
      ],
    });
  }

  // On élimine les sections vides (selon les rôles certaines peuvent l'être).
  return sections.filter((section) => section.items.length > 0);
};

const isItemActive = (item: NavItem, pathname: string): boolean => {
  if (!item.to) return false;
  if (item.to === '/dashboard') {
    return pathname === '/' || pathname === '/dashboard';
  }
  if (pathname === item.to || pathname.startsWith(`${item.to}/`)) return true;
  return (item.matchPrefixes || []).some(
    (prefix) => prefix !== '/' && (pathname === prefix || pathname.startsWith(`${prefix}/`)),
  );
};

const SidebarContent = ({
  roles,
  user,
  logout,
  collapsed,
  onToggleCollapsed,
  onMobileClose,
  isMobile,
}: Pick<SidebarProps, 'roles' | 'user' | 'logout' | 'collapsed' | 'onToggleCollapsed' | 'onMobileClose'> & {
  isMobile: boolean;
}) => {
  const { t } = useTranslation('menu');
  const navigate = useNavigate();
  const location = useLocation();
  // En mobile le menu est toujours déployé (overlay), pas d'icônes seules.
  const showLabels = isMobile || !collapsed;

  const sections = useMemo(() => buildSections(roles, t), [roles, t]);

  const isBloomerOnly = roles.includes(Role.Bloomer) && !roles.includes(Role.Admin) && !roles.includes(Role.System);
  const brandSubtitle = isBloomerOnly ? t('brand.freelance') : t('brand.backOffice');

  let userSubtitle = '';
  if (roles.includes(Role.System)) userSubtitle = t('roleLabel.system');
  else if (roles.includes(Role.Admin)) userSubtitle = t('roleLabel.admin');
  else if (roles.includes(Role.Bloomer)) userSubtitle = t('roleLabel.bloomer');

  const handleNavigate = (item: NavItem) => {
    if (item.onClick) {
      item.onClick();
    } else if (item.href) {
      window.open(item.href, '_blank', 'noopener,noreferrer');
    } else if (item.to) {
      void navigate(item.to);
    }
    if (isMobile) onMobileClose();
  };

  return (
    <Inner showLabels={showLabels}>
      <TopRow showLabels={showLabels}>
        <Brand>
          <LogoTile>
            <img src={bloomaysGlyph} alt="Bloomays" />
          </LogoTile>
          {showLabels && (
            <BrandText>
              <BrandName>Bloomays</BrandName>
              <BrandSub>{brandSubtitle}</BrandSub>
            </BrandText>
          )}
        </Brand>
        {!isMobile && (
          <CollapseButton
            type="button"
            collapsed={collapsed}
            aria-label={collapsed ? t('expandMenu') : t('collapseMenu')}
            title={collapsed ? t('expandMenu') : t('collapseMenu')}
            onClick={onToggleCollapsed}
          >
            <KeyboardDoubleArrowLeftOutlinedIcon fontSize="small" />
          </CollapseButton>
        )}
      </TopRow>

      <Nav>
        {sections.map((section) => (
          <Section key={section.key}>
            {showLabels ? <SectionTitle>{section.title}</SectionTitle> : <SectionDivider />}
            {section.items.map((item) => {
              const active = isItemActive(item, location.pathname);
              const button = (
                <NavItemButton
                  key={item.key}
                  type="button"
                  active={active}
                  showLabels={showLabels}
                  onClick={() => handleNavigate(item)}
                >
                  <NavIcon active={active}>{item.icon}</NavIcon>
                  {showLabels && <NavLabel active={active}>{item.label}</NavLabel>}
                </NavItemButton>
              );
              return showLabels ? (
                button
              ) : (
                <Tooltip key={item.key} title={item.label} placement="right" arrow>
                  {button}
                </Tooltip>
              );
            })}
          </Section>
        ))}
      </Nav>

      {user && (
        <Footer showLabels={showLabels}>
          {showLabels && (
            <UserBlock>
              <UserAvatar aria-label={user.displayName}>
                {user.picture ? <img src={user.picture} alt={user.displayName} /> : getInitials(user.displayName)}
              </UserAvatar>
              <UserText>
                <UserName title={user.displayName}>{user.displayName}</UserName>
                {userSubtitle && <UserSub>{userSubtitle}</UserSub>}
              </UserText>
            </UserBlock>
          )}
          {logout && (
            <Tooltip title={t('logout')} placement={showLabels ? 'top' : 'right'} arrow>
              <LogoutButton type="button" aria-label={t('logout')} onClick={() => logout()}>
                <LogoutOutlinedIcon fontSize="small" />
              </LogoutButton>
            </Tooltip>
          )}
        </Footer>
      )}
    </Inner>
  );
};

const Sidebar = ({ roles, user, logout, collapsed, onToggleCollapsed, mobileOpen, onMobileClose }: SidebarProps) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: EXPANDED_WIDTH,
            backgroundColor: SIDEBAR_BG,
            borderRight: `1px solid ${SIDEBAR_BORDER}`,
          },
        }}
      >
        <SidebarContent
          roles={roles}
          user={user}
          logout={logout}
          collapsed={false}
          onToggleCollapsed={onToggleCollapsed}
          onMobileClose={onMobileClose}
          isMobile
        />
      </Drawer>
    );
  }

  return (
    <Aside collapsed={collapsed} aria-label="navigation principale">
      <SidebarContent
        roles={roles}
        user={user}
        logout={logout}
        collapsed={collapsed}
        onToggleCollapsed={onToggleCollapsed}
        onMobileClose={onMobileClose}
        isMobile={false}
      />
    </Aside>
  );
};

export default Sidebar;
export { Sidebar };

const Aside = styled('aside', { shouldForwardProp: (prop) => prop !== 'collapsed' })<{ collapsed: boolean }>(
  ({ collapsed }) => ({
    flex: `0 0 ${collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH}px`,
    width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
    backgroundColor: SIDEBAR_BG,
    borderRight: `1px solid ${SIDEBAR_BORDER}`,
    height: '100%',
    minHeight: 0,
    transition: 'width 0.2s ease, flex-basis 0.2s ease',
    overflow: 'hidden',
  }),
);

const Inner = styled('div', { shouldForwardProp: (prop) => prop !== 'showLabels' })<{ showLabels: boolean }>(
  ({ showLabels }) => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: showLabels ? '20px 16px' : '20px 12px',
    gap: '20px',
  }),
);

const TopRow = styled('div', { shouldForwardProp: (prop) => prop !== 'showLabels' })<{ showLabels: boolean }>(
  ({ showLabels }) => ({
    display: 'flex',
    flexDirection: showLabels ? 'row' : 'column',
    alignItems: 'center',
    justifyContent: showLabels ? 'space-between' : 'center',
    gap: showLabels ? '8px' : '14px',
    minHeight: '40px',
  }),
);

const Brand = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  minWidth: 0,
}));

const LogoTile = styled('div')(() => ({
  width: '40px',
  height: '40px',
  flexShrink: 0,
  borderRadius: '11px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: BRAND_GRADIENT,
  boxShadow: `0 4px 12px ${BRAND_GLOW}`,
  img: {
    width: '22px',
    height: '22px',
    // Le glyphe officiel est noir : on le force en blanc sur la tuile de marque.
    filter: 'brightness(0) invert(1)',
  },
}));

const BrandText = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
}));

const BrandName = styled('span')(() => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: '16px',
  lineHeight: 1.25,
  color: '#ffffff',
  whiteSpace: 'nowrap',
}));

const BrandSub = styled('span')(() => ({
  fontFamily: '"Poppins Regular", sans-serif',
  fontSize: '12px',
  lineHeight: 1.25,
  color: TEXT_MUTED,
  whiteSpace: 'nowrap',
}));

const CollapseButton = styled('button', { shouldForwardProp: (prop) => prop !== 'collapsed' })<{ collapsed: boolean }>(
  ({ collapsed }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '30px',
    height: '30px',
    flexShrink: 0,
    borderRadius: '8px',
    border: `1px solid ${SIDEBAR_BORDER}`,
    background: 'transparent',
    color: TEXT_MUTED,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease, color 0.2s ease',
    svg: {
      transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease',
    },
    '&:hover': {
      backgroundColor: ITEM_HOVER,
      color: '#ffffff',
    },
  }),
);

const Nav = styled('nav')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '18px',
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
}));

const Section = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
}));

const SectionTitle = styled('span')(() => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  color: TEXT_MUTED,
  padding: '0 12px',
  marginBottom: '2px',
}));

const SectionDivider = styled('div')(() => ({
  height: '1px',
  backgroundColor: SIDEBAR_BORDER,
  margin: '4px 8px',
}));

const NavItemButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'showLabels',
})<{ active: boolean; showLabels: boolean }>(({ active, showLabels }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: showLabels ? 'flex-start' : 'center',
  gap: '12px',
  width: '100%',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  padding: showLabels ? '10px 12px' : '10px',
  borderRadius: '12px',
  background: active ? BRAND_GRADIENT : 'transparent',
  boxShadow: active ? `0 6px 16px ${BRAND_GLOW}` : 'none',
  transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
  '&:hover': {
    background: active ? BRAND_GRADIENT : ITEM_HOVER,
  },
}));

const NavIcon = styled('span', { shouldForwardProp: (prop) => prop !== 'active' })<{ active: boolean }>(
  ({ active }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: active ? '#ffffff' : TEXT_DEFAULT,
    svg: { fontSize: '22px' },
  }),
);

const NavLabel = styled('span', { shouldForwardProp: (prop) => prop !== 'active' })<{ active: boolean }>(
  ({ active }) => ({
    fontFamily: active ? '"Poppins SemiBold", sans-serif' : '"Poppins Medium", sans-serif',
    fontSize: '14px',
    lineHeight: 1.2,
    color: active ? '#ffffff' : TEXT_DEFAULT,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
);

const Footer = styled('div', { shouldForwardProp: (prop) => prop !== 'showLabels' })<{ showLabels: boolean }>(
  ({ showLabels }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: showLabels ? 'space-between' : 'center',
    gap: '8px',
    paddingTop: '14px',
    borderTop: `1px solid ${SIDEBAR_BORDER}`,
  }),
);

const UserBlock = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  minWidth: 0,
}));

const UserAvatar = styled('div')(() => ({
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  overflow: 'hidden',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: AVATAR_GRADIENT,
  color: '#ffffff',
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: '13px',
  img: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}));

const UserText = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
}));

const UserName = styled('span')(() => ({
  fontFamily: '"Poppins Medium", sans-serif',
  fontSize: '13px',
  lineHeight: 1.3,
  color: '#ffffff',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const UserSub = styled('span')(() => ({
  fontFamily: '"Poppins Regular", sans-serif',
  fontSize: '11px',
  lineHeight: 1.3,
  color: TEXT_MUTED,
  whiteSpace: 'nowrap',
}));

const LogoutButton = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '34px',
  height: '34px',
  flexShrink: 0,
  borderRadius: '8px',
  border: `1px solid ${SIDEBAR_BORDER}`,
  background: 'transparent',
  color: TEXT_MUTED,
  cursor: 'pointer',
  transition: 'background-color 0.2s ease, color 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 51, 40, 0.15)',
    color: theme.palette.red[60],
    borderColor: 'rgba(255, 51, 40, 0.35)',
  },
}));
